import { Plus } from "lucide-react";
import type { TFunction } from "i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export type SummaryMatchedRow = {
  catalogItemId: string;
  label: string;
  amountUYU: number;
  amountUSD: number;
};

export type SummaryUnmatchedRow = {
  description: string;
  amountUYU: number;
  amountUSD: number;
};

type ClientSummaryPeriodBlockProps = {
  matchedTitle: string;
  matchedEmptyText: string;
  unmatchedTitle: string;
  matched: SummaryMatchedRow[];
  unmatched: SummaryUnmatchedRow[];
  locale: string;
  formatMoney: (currency: string, amount: number, locale: string) => string;
  t: TFunction;
  unmatchedSelected: Set<string>;
  onToggleUnmatchedRow: (description: string) => void;
  onUnmatchedHeaderCheckboxChange: (checked: boolean | "indeterminate") => void;
  unmatchedHeaderCheckboxState: boolean | "indeterminate";
  selectedUnmatchedRows: SummaryUnmatchedRow[];
  onAddCatalogFromUnmatched: (row: SummaryUnmatchedRow) => void;
  onAddSelectedUnmatchedToCatalog: () => void;
};

export function ClientSummaryPeriodBlock({
  matchedTitle,
  matchedEmptyText,
  unmatchedTitle,
  matched,
  unmatched,
  locale,
  formatMoney,
  t,
  unmatchedSelected,
  onToggleUnmatchedRow,
  onUnmatchedHeaderCheckboxChange,
  unmatchedHeaderCheckboxState,
  selectedUnmatchedRows,
  onAddCatalogFromUnmatched,
  onAddSelectedUnmatchedToCatalog,
}: ClientSummaryPeriodBlockProps): JSX.Element {
  const hasMatched = matched.length > 0;
  const hasUnmatched = unmatched.length > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-foreground">{matchedTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {!hasMatched ? (
            <p className="text-sm text-muted-foreground">{matchedEmptyText}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("clients.detail.summary.columnProductService")}</TableHead>
                  <TableHead className="text-right">{t("clients.detail.summary.columnUyu")}</TableHead>
                  <TableHead className="text-right">{t("clients.detail.summary.columnUsd")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matched.map((row) => (
                  <TableRow key={row.catalogItemId}>
                    <TableCell className="max-w-[min(100vw-8rem,28rem)] whitespace-pre-line break-words font-medium text-foreground">
                      {row.label}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney("UYU", row.amountUYU, locale)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney("USD", row.amountUSD, locale)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {hasUnmatched ? (
        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <CardTitle className="text-foreground">{unmatchedTitle}</CardTitle>
            {selectedUnmatchedRows.length > 0 ? (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="shrink-0 transition-colors duration-150"
                onClick={onAddSelectedUnmatchedToCatalog}
              >
                <Plus className="mr-1.5 h-4 w-4 shrink-0" aria-hidden />
                {t("clients.detail.summary.addSelectedToCatalog")}
              </Button>
            ) : null}
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[3rem]">
                    <Checkbox
                      checked={unmatchedHeaderCheckboxState}
                      onCheckedChange={onUnmatchedHeaderCheckboxChange}
                      aria-label={t("clients.detail.summary.selectAllUnmatchedLines")}
                      className="transition-colors duration-150"
                    />
                  </TableHead>
                  <TableHead>{t("clients.detail.summary.columnDescription")}</TableHead>
                  <TableHead className="text-right">{t("clients.detail.summary.columnUyu")}</TableHead>
                  <TableHead className="text-right">{t("clients.detail.summary.columnUsd")}</TableHead>
                  <TableHead className="w-[1%] whitespace-nowrap text-right">
                    {t("clients.detail.summary.columnActions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmatched.map((row, index) => (
                  <TableRow key={`${row.description}-${index}`}>
                    <TableCell className="align-top">
                      <Checkbox
                        checked={unmatchedSelected.has(row.description)}
                        onCheckedChange={() => onToggleUnmatchedRow(row.description)}
                        aria-label={t("clients.detail.summary.selectUnmatchedLine")}
                        className="transition-colors duration-150"
                      />
                    </TableCell>
                    <TableCell className="max-w-[min(100vw-8rem,28rem)] whitespace-pre-line break-words font-medium text-foreground">
                      {row.description}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney("UYU", row.amountUYU, locale)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney("USD", row.amountUSD, locale)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="transition-colors duration-150"
                        onClick={() => onAddCatalogFromUnmatched(row)}
                      >
                        <Plus className="mr-1.5 h-4 w-4 shrink-0" aria-hidden />
                        {t("clients.detail.summary.addToCatalog")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
